import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import dayjs, { Dayjs } from 'dayjs'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { invalidError, notice } from '@/components/pbo/notice'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { FETCH_ALL_MESSAGES } from '@/hooks/useFetchAllRows'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, ShipperScope, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { ShipperOptionSource, buildShipperNameOptions } from '@/components/system/shipperSelectFilter'
import { getShipperListV2 } from '@/services/shipper/apis'
import { ShipperItem } from '@/services/shipper/types'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { useLesInboundCancelGate } from '@/services/inbound/lesOrigin/useLesInboundCancelGate'
import { GET_INBOUNDS_EXCEL_URL, GET_INBOUND_DETAILS_EXCEL_URL, getInboundDetails } from '@/services/inbound/inboundInquiry/apis'
import { inboundInquiryQueryKeys, InboundsCondition, useCancelInbound, useGetInbounds } from '@/services/inbound/inboundInquiry/useInboundInquiryQueries'
import {
  CancelInboundReq,
  GetInboundDetailsItem,
  GetInboundDetailsReq,
  GetInboundsItem,
} from '@/services/inbound/inboundInquiry/types'
import {
  DATE_FORMAT,
  DEFAULT_SERVICE_TYPE,
  EXCEL_ROW_LIMIT,
  GRID_CDS,
  MESSAGES,
  ORDER_TYPE_OPTIONS,
  REASON_DETAIL_MAX_LENGTH,
  REASON_DETAIL_MIN_LENGTH,
  SHIPPER_LIST_ALL_SIZE,
  SHIPPER_TYPE_OPTIONS,
} from './constants'
import { fromSearchQuery, toSearchQuery } from './searchQuery'
import {
  InboundInquiryFormValues,
  exceedsOrdOptionNumberLimit,
  hasRequiredFilter,
  hasTooManyShipperNames,
  toDetailedReason,
  toInboundsReq,
  toTrimmedList,
} from './filterToRequest'
import { DEFAULT_PRINT_COPIES, DEFAULT_PRINT_SPEC, IMPLEMENTED_SPECS, isImplementedSpec, openBarcodePrintWindow, printableRows } from './components/barcodePrint'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import InboundInquiryPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 조회영역 값 키 — 생성본이 낸 것 그대로(화주 짝 필드는 `"inqShipper.types"`·`"inqShipper.names"`, 나머지는 filterKey). */
const FK = {
  types: 'inqShipper.types',
  names: 'inqShipper.names',
  plnDate: 'inqPlnDate',
  ordOptNo: 'ORD_OPT_NO',
  ordType: 'ORD_TYPE',
  actDate: 'ACT_DATE',
  whNm: 'WH_NM',
} as const

/** 표 그룹 id — 생성본 `<SelBridge scope="…">`. 위(입고 목록)·아래(품목) 선택을 이것으로 가른다(가이드 52 §5-A 12). */
const HD_SCOPE = 'hd-tableGroup'
const IT_SCOPE = 'it-tableGroup'

const today = (): string => dayjs().format(DATE_FORMAT)

/** 옵션 슬롯은 렌더마다 불린다 — 상수 목록은 모듈에서 한 번만 만들어 참조를 고정한다. */
const ORDER_TYPE_OPTS: Option[] = [...ORDER_TYPE_OPTIONS]
// 바코드 출력 규격은 실제로 인쇄되는 것만 — 데모의 라벨(1X2)는 인쇄 정의가 없어 고르면 막힌다(barcodePrint.ts).
const PRINT_SPEC_OPTS: Option[] = IMPLEMENTED_SPECS.map((v) => ({ value: v, label: v }))

/** 조회 실패(HTTP 400) 응답에서 서버 메시지를 꺼낸다 — 2.0 StandardResponse 는 meta.message 에 싣는다. */
const extractErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data
  return data?.meta?.message || data?.message || fallback
}

/** HEAD 응답 `clsDate`(`YYYY-MM-DD`) → 취소 요청 `cutDate`(`yyyyMMdd`). */
const toCutDate = (clsDate: string): string => dayjs(clsDate).format('YYYYMMDD')

/** 다건 입력·멀티셀렉트 값 — 뷰는 배열 또는 undefined 로 준다(`filterToRequest.toTrimmedList` 와 같은 규칙). */
const asList = toTrimmedList
const asText = (v: unknown): string => (typeof v === 'string' ? v : '')
const asDate = (v: unknown): string | undefined => {
  if (!v) return undefined
  const d = dayjs(v as dayjs.ConfigType)
  return d.isValid() ? d.format(DATE_FORMAT) : undefined
}

/**
 * 뷰 조회영역 값(키 = 생성본) → 옛 폼 값(`InboundInquiryFormValues`) — `filterToRequest.ts`·`searchQuery.ts` 를 그대로 잇는다.
 * 입고예정일자가 비어 있으면 오늘이다 — 뷰가 값이 없을 때 그리는 칩(defaultPeriod 'today')과 같은 값이라 표시와 조회가 어긋나지 않는다.
 * 대표입고번호(`orderNumbers`)는 데모 조회영역에 없어 싣지 않는다(입고 3화면과 같은 결정 — 2026-09-08 PD 확정).
 */
const toFormValues = (f: Record<string, unknown>): InboundInquiryFormValues => {
  const range = Array.isArray(f[FK.plnDate]) ? (f[FK.plnDate] as unknown[]) : undefined
  const from = asDate(range?.[0]) ?? today()
  const to = asDate(range?.[1]) ?? from
  const act = asDate(f[FK.actDate])
  return {
    serviceTypes: asList(f[FK.types]),
    shipperCode: asList(f[FK.names]),
    plnDateRange: [dayjs(from), dayjs(to)],
    ordOptionNumbers: asList(f[FK.ordOptNo]),
    orderNumbers: [],
    orderType: asText(f[FK.ordType]),
    actDate: act ? dayjs(act) : null,
    warehouseName: asText(f[FK.whNm]),
  }
}

/** 옛 폼 값(복원값) → 뷰 조회영역 값. 없는 값은 얹지 않는다(뷰 기본값이 살아 있게). */
const toFilterValues = (v: InboundInquiryFormValues | undefined): Record<string, unknown> => {
  if (!v) return {}
  const pln = v.plnDateRange
  const [plnFrom, plnTo] = Array.isArray(pln) ? [pln[0], pln[1]] : [pln, pln]
  const from = asDate(plnFrom)
  const to = asDate(plnTo)
  const act = asDate(Array.isArray(v.actDate) ? (v.actDate as (Dayjs | null)[])[0] : v.actDate)
  const codes = asList(v.ordOptionNumbers)
  return {
    ...(v.serviceTypes?.length ? { [FK.types]: v.serviceTypes } : {}),
    ...(v.shipperCode?.length ? { [FK.names]: v.shipperCode } : {}),
    ...(from && to ? { [FK.plnDate]: [from, to] } : {}),
    ...(codes.length ? { [FK.ordOptNo]: codes } : {}),
    ...(v.orderType ? { [FK.ordType]: v.orderType } : {}),
    ...(act ? { [FK.actDate]: act } : {}),
    ...(v.warehouseName ? { [FK.whNm]: v.warehouseName } : {}),
  }
}

/** HEAD 행 키 — `ordOptionNumber`·`clsDate`·`cutSeq` 조합(ITEM 조회·취소가 쓰는 같은 세 값). 뷰의 마스터-디테일 조인 키(`__pick`)로도 쓴다. */
export const toHeadRowKey = (row: Pick<GetInboundsItem, 'ordOptionNumber' | 'clsDate' | 'cutSeq'>): string =>
  `${row.ordOptionNumber}|${row.clsDate}|${row.cutSeq}`

/**
 * API 행 → 뷰 행. 뷰 컬럼 키는 데모(`COLUMNS` dataIndex)의 것이라 응답 필드명(camelCase)을 그 키로 옮긴다(가이드 52 §5-A 1).
 * `key` = 행 id(안 실으면 배열 인덱스가 되어 재조회 뒤 체크 선택이 다른 행에 붙는다) · `__pick` = 마스터-디테일 조인 키 · `__src` = 원본(액션이 쓴다).
 */
const toHeadRow = (r: GetInboundsItem): Row => {
  const key = toHeadRowKey(r)
  return {
    key,
    __pick: key,
    __src: r,
    service_type: r.serviceType,
    SPR_NM: r.shipperCode,
    shipper_name: r.shipperName,
    ORD_OPT_NO: r.ordOptionNumber,
    PLN_DATE: r.plnDate,
    ORD_STATUS: r.ordStatus,
    ORD_QTY: r.ordQty,
    ACT_QTY: r.actQty,
    WH_NM: r.warehouseName,
    ORD_TYPE: r.orderType,
    TRS_TYPE: r.trsType,
    PROC_NM: r.procNm,
    INS_DATE: r.createdAt,
    INS_BY: r.createdBy,
    UPD_DATE: r.updatedAt,
    UPD_BY: r.updatedBy,
    ACT_DATE: r.actDate,
  }
}

/**
 * ITEM 응답 → 뷰 행. 단위코드(`ULD_TYPE`)만 BE 응답에 없어 빈 칸이다(D8 — 컬럼은 남기고 값은 없다). 주문일자는 옛 화면과 같이 `plnDate` 다.
 */
const toItemRow = (r: GetInboundDetailsItem, pick: string, i: number): Row => ({
  key: `${pick}|${r.ordOptNo}-${r.ordOptNoSeq ?? i}`,
  __pick: pick,
  __src: r,
  service_type: r.serviceType,
  shipper_name: r.shipperName,
  SPR_NM: r.shipperCode,
  PLN_DATE: r.plnDate,
  ORD_TYPE: r.ordType,
  TRS_TYPE: r.trsType,
  BRD_NM: r.brdNm,
  STL_NO: r.stlNo,
  GDS_NM: r.gdsNm,
  uid: r.uid,
  BARCODE: r.barcode,
  SKU_ID: r.skuId,
  GDS_OPT: r.gdsOpt,
  ORD_QTY: r.ordQty,
  ACT_QTY: r.actQty,
  GDS_CD: r.gdsCd,
  ORD_OPT_NO: r.ordOptNo,
  ORD_STATUS: r.ordStatus,
  ORD_DATE: r.plnDate,
  ACT_DATE: r.actDate,
  TRS_TYPE_DETAIL: r.trsTypeDetail,
  TRS_LSP_NM: r.trsLspNm,
  TRS_NUMBER: r.trsNumber,
  TRS_DEPT_DT: r.trsDeptDt,
  TRS_ARRV_DT: r.trsArrvDt,
  PROC_NM: r.procNm,
  ORD_OPT_NO_SEQ: r.ordOptNoSeq,
  brand_id: r.brandId,
  option_code: r.optionCode,
  GDS_GROUP1: r.gdsGroup1,
  DRV_NAME: r.drvName,
  REF_NO: r.refNo,
  REF_OPT_NO: r.refOptNo,
  LOT_ATTR5: r.lotAttr5,
  INS_DATE: r.insDate,
  INS_BY: r.insBy,
  UPD_DATE: r.updDate,
  UPD_BY: r.updBy,
})

/** 뷰 행에 실어 둔 원본(`__src`)만 꺼낸다 — 뷰가 넘기는 행은 표시용 사본이다. */
const srcOf = <T,>(rows: readonly Row[]): T[] => rows.map((r) => r.__src as T | undefined).filter((s): s is T => s != null)
/**
 * 상단(입고 목록) 행의 원본인가 — 품목 행(`GetInboundDetailsItem`)에는 `clsDate` 가 없다는 계약(types.ts)에 기댄다.
 * ITEM 응답에 `clsDate` 가 추가되면 이 판정이 소리 없이 깨져 품목 행이 상단 행으로 분류된다 — API 변경 리뷰 때 함께 본다.
 */
const isHeadSrc = (src: unknown): src is GetInboundsItem => typeof src === 'object' && src !== null && 'clsDate' in src
/** 하단(품목) 행의 원본인가 — 부정(!isHeadSrc)이 아니라 품목에만 있는 필드(`barcode`)로 판정해 제3의 행이 섞여도 품목으로 오인하지 않는다. */
const isItemSrc = (src: unknown): src is GetInboundDetailsItem => typeof src === 'object' && src !== null && 'barcode' in src && !('clsDate' in src)

/** 하단 표 체크 선택 한 행 — 어느 입고 건(상단 행 key = __pick)의 품목인지와 원본. */
type ItemSel = { pick: string; src: GetInboundDetailsItem }

/**
 * 화면 진입·초기화 시의 조회 조건 기본값(뷰 키).
 *
 * 화주명은 기본 화주(`defaultShipper` — 재고통합 대상 계정은 MUSINSA, LOGISPRD-15718)다.
 * 화주유형은 계정 성격에 따라 다르다. 화주 고정 계정은 기본 화주의 서비스유형이 정답이라 화주 목록 응답이 온 뒤 `filterFallback` 이 채우고
 * (응답 전에는 빈 값), 통합 운영 계정은 고를 수 있는 값이 6종이라 정답이 없어 [DEFAULT_SERVICE_TYPE] 로 연다.
 */
const defaultFilterValues = (isFreeShipper: boolean, defaultShipperCode: string): Record<string, unknown> => ({
  [FK.types]: isFreeShipper ? [DEFAULT_SERVICE_TYPE] : [],
  [FK.names]: defaultShipperCode ? [defaultShipperCode] : [],
  [FK.plnDate]: [today(), today()],
})

/**
 * 입고조회 2.0(GR09) — 1.0 GR02 를 pbo-core 로 재구현한 화면(LOGISPRD-12099).
 *
 * 겉(조회영역·위아래 표·입고취소 확인창·엑셀 사유 모달·바코드 출력 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가
 * 그리고, 이 파일은 옛 훅·API·세션/URL 유지·화주 잠금·검증을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 옛 조립부(`InboundInquiryPage` + `components/*` + `hooks/useInboundInquiryScreen`)가 나눠 갖던 상태를 여기로 모았다 — 표 선택·목록 검색·
 * 페이저·모달 열림·조회영역 폼은 뷰가 가지므로 그 짝(resetToken 등)은 지웠다.
 *
 * 옛 화면과 달라진 것(데모가 정본):
 * - 마스터-디테일은 상단 표 **행 클릭**(pick — 재클릭 해제, 하이라이트)을 따라간다(옛 화면은 행 더블클릭). 체크박스는 [입고취소] 대상 선택이다.
 *   뷰는 한 건만 pick 하지만 슬롯은 배열이라 여러 건도 받는다(합쳐 보임). [조회]·[초기화]는 뷰가 pick 을 푼다(onPick([])).
 * - 대표입고번호 필터(플래그 뒤)는 데모 조회영역에 없어 빠졌다(입고 3화면과 같은 결정).
 *
 * 조회조건 유지(요구사항 3)는 화면별 `searchQuery.ts` + `MenuStorage.menuOnSession` + URL 쿼리 복원 조합이다(D16).
 */
/** 화주유형 옵션 빈 목록 — 옵션 슬롯은 렌더마다 불리므로 참조를 고정한다. */
const EMPTY_OPTIONS: Option[] = []

function InboundInquiry({ coMingle }: Readonly<{ coMingle: boolean }>) {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const queryClient = useQueryClient()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()

  /**
   * 통합 운영 화주(FREE_SHIPPERS 4종)만 화주를 자유롭게 고른다. 화주 고정 계정은 목록 요청·옵션·복원값 셋 다 본인 화주로 좁힌다
   * (GR05 `allInboundStatus/index.tsx` 준용). 마운트 스냅샷은 useRef 로 — useMemo 는 캐시라 React 가 언제든 버릴 수 있다.
   * 재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다 — 셋 다 고를 수 있는 화주로 넓힌다(LOGISPRD-15608, `withCoMingle`).
   */
  const sessionRef = useRef<{
    isFreeShipper: boolean
    ownShipperName: string
    viewerShipperCode: string
    defaultShipperCode: string
    scope: ShipperScope
    selectableNames: string[]
  }>()
  if (!sessionRef.current) {
    const user = AuthStorage.user.get()
    // 잠금 판정은 2.0 공용 규칙 — FREE_SHIPPERS 4종 외는 자기 화주로 고정(LOGISPRD-14652)
    const scope = withCoMingle(shipperScopeOf(user), coMingle)
    const ownShipperName = user.shipperName ? String(user.shipperName) : ''
    sessionRef.current = {
      isFreeShipper: !scope.locked,
      ownShipperName,
      viewerShipperCode: scope.shipperCode,
      // 화주명 기본값 — 재고통합 대상 계정은 MUSINSA, 그 밖은 자기 화주다(LOGISPRD-15718)
      defaultShipperCode: defaultShipper(scope).shipperCode,
      scope,
      // 화주 목록 요청은 화주명으로 거른다 — 자기 화주는 세션 화주명(shipperName)이다
      selectableNames: selectableShippers(scope).map((s) => (s.shipperCode === scope.shipperCode ? ownShipperName : s.shipperName)).filter(Boolean),
    }
  }
  const { isFreeShipper, ownShipperName, viewerShipperCode, defaultShipperCode, scope, selectableNames } = sessionRef.current
  /** 잠금 계정의 화주명 값 — 고를 수 있는 화주만 남기고, 하나도 없으면 기본 화주다. 2개 이상은 그대로 둬 조회가 막히게 한다 */
  const lockedShipperCodes = (codes: string[]): string[] => {
    const kept = codes.filter((c) => scopedShipperCode(scope, c) === c)
    if (kept.length) return kept
    return defaultShipperCode ? [defaultShipperCode] : []
  }

  /**
   * URL 쿼리 복원(마운트 시 한 번). 화주 고정 계정은 주소를 손대면 남의 화주 조건으로 들어올 수 있어 기본 화주로 덮고, 화주유형은
   * 비운다 — 이 시점엔 화주 목록 응답 전이라 자기 유형을 모르므로, 응답이 온 뒤 `filterFallback`·`restoredValues` 가 실제 유형을 심는다.
   */
  const restoredRef = useRef<ReturnType<typeof fromSearchQuery>>()
  const restoredReadRef = useRef(false)
  if (!restoredReadRef.current) {
    restoredReadRef.current = true
    const r = fromSearchQuery(parsedQs)
    restoredRef.current =
      !r || isFreeShipper ? r : { ...r, values: { ...r.values, shipperCode: lockedShipperCodes(asList(r.values.shipperCode).slice(0, 1)), serviceTypes: [] } }
  }
  const restored = restoredRef.current

  // 세션 화주의 실제 서비스유형을 알아야 기본값을 채울 수 있다(GR05 준용). 화주 고정 계정은 목록 요청 자체를 본인 화주 하나로 좁힌다.
  const { data: allShippers = [], isLoading: isShipperListLoading } = useQuery<ShipperItem[]>({
    meta: { errorToast: true }, // 실패는 옛 알럿 대신 새 디자인 알럿(queryClient.ts → notice.failure)
    queryKey: inboundInquiryQueryKeys.shippers(isFreeShipper ? 'ALL' : selectableNames.join(',')),
    queryFn: async (): Promise<ShipperItem[]> => {
      const res = await getShipperListV2({
        shipperNames: isFreeShipper ? [] : selectableNames,
        logisticsServiceTypes: [],
        page: 0,
        size: SHIPPER_LIST_ALL_SIZE,
      })
      return res?.data?.items ?? []
    },
    enabled: isFreeShipper || !!ownShipperName,
    staleTime: API_COMMON_CACHE_TIME,
  })
  /** 화주 고정 계정이 고를 수 있는 화주코드 → 그 화주의 유형. 목록이 오기 전엔 비어 있다 */
  const serviceTypeByCode = useMemo(
    () =>
      new Map(
        isFreeShipper ? [] : selectableShippers(scope).map((s) => [s.shipperCode, allShippers.find((a) => a.shipperCode === s.shipperCode)?.logisticsServiceType || undefined] as const),
      ),
    [isFreeShipper, scope, allShippers],
  )
  const ownServiceType = serviceTypeByCode.get(viewerShipperCode)
  /** 화주 고정 계정이 지금 고른 화주(첫 값)의 유형 — 고를 수 없는 화주면 기본 화주의 유형이다 */
  const lockedServiceTypeOf = useCallback((codes: unknown) => serviceTypeByCode.get(scopedShipperCode(scope, asList(codes)[0])), [scope, serviceTypeByCode])

  /**
   * 화주명 옵션의 원본. 화주 고정 계정은 API 가 다른 화주를 섞어 돌려줘도 고를 수 있는 화주만 남긴다(화주 스코프).
   * 목록에 없는 화주는 세션 값으로 한 줄을 만든다 — 자기 화주는 세션 화주명이 없으면 만들지 않는다(종전 그대로).
   */
  const shipperOptionSources = useMemo<ShipperOptionSource[]>(() => {
    if (isFreeShipper) return allShippers
    return selectableShippers(scope).flatMap((selectable) => {
      const rows = allShippers.filter((s) => s.shipperCode === selectable.shipperCode)
      if (rows.length > 0) return rows
      if (selectable.shipperCode !== viewerShipperCode) return [{ ...selectable, logisticsServiceType: '' }]
      return ownShipperName ? [{ shipperCode: viewerShipperCode, shipperName: ownShipperName, logisticsServiceType: ownServiceType ?? '' }] : []
    })
  }, [allShippers, isFreeShipper, scope, ownShipperName, viewerShipperCode, ownServiceType])

  /**
   * 화주유형 옵션. 화주 고정 계정은 고른 화주의 유형 하나 — 남의 유형을 고르면 화주명 목록이 비어 자기 화주를 잃는다(LOGISPRD-12609).
   * 옵션 슬롯은 렌더마다 불리므로 화주코드별로 참조를 고정한다.
   */
  const serviceTypeOptions = useMemo<Option[]>(() => [...SHIPPER_TYPE_OPTIONS], [])
  const lockedServiceTypeOptions = useMemo(
    () => new Map(Array.from(serviceTypeByCode, ([code, type]): [string, Option[]] => [code, type ? [{ value: type, label: type }] : EMPTY_OPTIONS])),
    [serviceTypeByCode],
  )

  /** 복원 조건을 만들 때 쓰는 폼 값 — 화주 고정 계정은 뒤늦게 온 복원 화주의 유형을 심는다(리뷰 지적: 화면엔 유형이 보이는데 조회엔 빠지던 것). */
  const restoredValues = useMemo(() => {
    if (!restored) return undefined
    const restoredType = isFreeShipper ? undefined : lockedServiceTypeOf(restored.values.shipperCode)
    if (!restoredType) return restored.values
    return { ...restored.values, serviceTypes: [restoredType] }
  }, [restored, isFreeShipper, lockedServiceTypeOf])
  // 복원된 조건으로 만든 조회 요청. 필수 필터를 못 채우면 버린다 — 그대로 보내면 BE 가 400 을 준다.
  const restoredCondition = useMemo(() => {
    if (!restoredValues) return undefined
    const condition = toInboundsReq(restoredValues)
    return hasRequiredFilter(condition) ? condition : undefined
  }, [restoredValues])

  /**
   * HEAD 조회 조건 — null 이면 조회하지 않는다. 복원 조건은 화주 목록을 기다려 아래 effect 가 세운다
   * (화주 고정 계정의 복원 조건에 복원 화주의 유형이 들어간다 — 먼저 세우면 덜 찬 조건으로 한 번 더 받는다).
   * 전량을 받아 화면이 1만 건씩 나눈다 — 페이지 이동은 다시 조회하지 않는다(LOGISPRD-15268).
   */
  const [searchCondition, setSearchCondition] = useState<InboundsCondition | null>(null)
  /** 뷰가 마지막으로 알린 목록 검색어 — onState 는 마운트·조회 뒤에도 불리므로 실제로 바뀐 것만 저장한다. */
  const keywordRef = useRef(restored?.keyword ?? '')
  /** 마지막으로 **조회에 쓴** 폼 값. 목록 검색어 확정은 이 값과 함께 저장한다. */
  const lastSearchValuesRef = useRef<InboundInquiryFormValues>(restored?.values ?? toFormValues(defaultFilterValues(isFreeShipper, defaultShipperCode)))
  /** 복원 조회를 이미 처리했는가 — 복원 effect 와 조회 슬롯이 함께 잠근다. */
  const isRestoreSearchedRef = useRef(false)

  const { rows: headItems, totalCount: headTotal, isFirstLoading: isHeadFirstLoading, isFetchingRest: isHeadFetchingRest, refetch: refetchHead, guardAction } = useGetInbounds(searchCondition)
  const headRows = useMemo(() => headItems.map(toHeadRow), [headItems])

  /** 마스터-디테일 — 상단 표에서 **체크로 고른** 입고 건(뷰의 onPick)의 품목을 읽는다. 여러 건이면 합쳐 보인다. */
  const [picked, setPicked] = useState<GetInboundsItem[]>([])
  const detailParams = useMemo<(GetInboundDetailsReq | null)[]>(
    () =>
      picked.map((h) =>
        h.cutSeq == null
          ? null
          : {
              // 선택 행 자체의 화주를 우선한다 — 화주유형만으로 조회하면 searchCondition.shipperCode 가 비어 undefined 가 나간다.
              shipperCode: h.shipperCode ?? searchCondition?.shipperCode,
              ordOptionNumber: h.ordOptionNumber,
              clsDate: h.clsDate,
              cutSeq: h.cutSeq,
            },
      ),
    [picked, searchCondition?.shipperCode],
  )
  const details = useQueries({
    queries: detailParams.map((params) => ({
      queryKey: inboundInquiryQueryKeys.item(params),
      queryFn: async (): Promise<GetInboundDetailsItem[]> => {
        const res = await getInboundDetails(params as GetInboundDetailsReq)
        return res.data?.items ?? []
      },
      enabled: params !== null,
      // 실패 문구는 아래 effect 가 서버 meta.message 로 띄운다 — 전역 QueryCache.onError 까지 뜨면 문구가 달라 알럿이 두 번 쌓인다(useGetInbounds 와 같은 이유, 리뷰 2026-09-29).
      meta: { noErrorMessage: true },
    })),
    // 결과 배열은 렌더마다 새 객체다 — combine 으로 구조 공유해 표 갱신 effect 가 돌지 않게 한다.
    //  results[i] ↔ detailParams[i] ↔ picked[i] — 셋 다 picked.map 으로 만든 같은 길이 배열이라 인덱스로 짝을 맞춘다.
    combine: (results) => ({
      rows: results.flatMap((r, i) => (r.data ?? []).map((it, j) => toItemRow(it, toHeadRowKey(picked[i]), j))),
      loading: results.some((r) => r.isFetching),
      // 실패한 조회의 오류(첫 건) — 빈 배열로 삼키면 '품목이 없는 건' 과 '조회 실패' 를 구분할 수 없다(리뷰 2026-09-29). 오류 객체는 실패 상태가 유지되는 동안 같은 참조다.
      error: results.find((r) => r.isError)?.error ?? null,
    }),
  })
  useEffect(() => {
    if (details.error) notice.failure(extractErrorMessage(details.error, MESSAGES.itemSearchFailed))
  }, [details.error])

  /** 표별 체크 선택(뷰의 onSelect, scope 로 가른다) — 액션의 ctx.selected 는 마지막으로 바뀐 스코프 하나라 이 값을 먼저 쓴다(가이드 52 §5-A 3). */
  const [selectedHeads, setSelectedHeads] = useState<GetInboundsItem[]>([])
  // 품목 선택은 어느 입고 건(__pick)의 행인지 함께 든다 — 뷰의 하단 표는 건이 바뀌어도 체크를 지우지 않으므로(pbo-core Table 내부 state),
  // 건 클릭 때 비우면 같은 건으로 돌아왔을 때 체크는 보이는데 인쇄 대상에서 빠진다(리뷰 2026-09-29). 값은 뷰가 알린 선택을 그대로 두고 지금 고른 건의 행만 골라 쓴다.
  const [selectedItems, setSelectedItems] = useState<ItemSel[]>([])

  /** 센터명 — 공통코드 콤보(WH_NM). 'ALL'(전체) 행은 BE 가 `warehouseName` 완전일치라 고르면 항상 0건이어서 뺀다. */
  const { data: combos } = useGetCombos(['WH_NM'], { errorToast: true })
  const warehouseNameOptions = useMemo<Option[]>(() => toSelectOptions(excludeAllOption(combos?.WH_NM)), [combos])

  /** 엑셀 다운로드 사유코드 — 공용 콤보(DOWNLOAD_CD). 명이 비면 코드로 대체한다. 데모 옵션이 그대로 나가면 감사로그에 없는 코드가 실린다. */
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo<Option[]>(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, i) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[i] || code,
      })),
    [exportCombo],
  )

  /** 조회 상태를 메뉴 세션과 URL 에 남긴다. 탭바가 갱신되도록 `reload()` 를 함께 부른다(D16). */
  const persistSearch = useCallback(
    (values: InboundInquiryFormValues, nextKeyword: string) => {
      const query = toSearchQuery(values, nextKeyword)
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery],
  )

  /**
   * 복원된 조건이 있으면 진입 때 그 조건을 세운다 — 목록까지 되살아나야 "보던 화면으로 돌아온다".
   * 화주 목록을 기다린다(화주 고정 계정의 복원 조건에 복원 화주의 유형이 들어간다). 캐시가 있으면(탭 복귀) 그대로 그리고,
   * 받다 만 캐시면 남은 구간을 이어 받고, 없으면 받는다(공용 훅 staleTime: Infinity — 탭 복귀에 다시 받지 않는다).
   */
  useEffect(() => {
    if (isShipperListLoading || !restoredCondition || isRestoreSearchedRef.current) return
    isRestoreSearchedRef.current = true
    setSearchCondition(restoredCondition)
  }, [isShipperListLoading, restoredCondition])

  // ── 입고취소 ──────────────────────────────────────────────
  const lesGate = useLesInboundCancelGate()
  const { mutateAsync: cancelInboundMutateAsync } = useCancelInbound()
  const [isCancelling, setIsCancelling] = useState(false)

  /**
   * 액션 대상 — 스코프별 선택을 먼저 쓰고, 없으면 ctx.selected 에서 원본을 꺼낸다.
   * ctx.selected 는 **마지막으로 비어 있지 않던 스코프**의 선택이라 상단 행이 섞여 올 수 있다 — 원본 모양(`clsDate` 유무)으로 걸러
   * 상단만 체크한 채 [바코드 인쇄]를 누르면 가드가 열리지 않게 한다(봇 리뷰 Warning 2026-09-29).
   */
  const headTargets = useCallback(
    (ctx: ActionCtx): GetInboundsItem[] => (selectedHeads.length ? selectedHeads : srcOf<unknown>(ctx.selected).filter(isHeadSrc)),
    [selectedHeads],
  )
  const itemTargets = useCallback(
    (ctx: ActionCtx): GetInboundDetailsItem[] => {
      // 배선 선택·뷰가 넘기는 선택(ctx.selected = 모듈 변수 selRef, 배선이 비울 수 없다) 모두 지금 고른 건(__pick)의 품목만 받는다 —
      // 다른 건에서는 빠지고, 같은 건으로 돌아오면(체크가 그대로 보이는 상태) 다시 대상이 된다.
      const pickedKeys = new Set(picked.map(toHeadRowKey))
      const own = selectedItems.filter((s) => pickedKeys.has(s.pick)).map((s) => s.src)
      if (own.length) return own
      return srcOf<unknown>(ctx.selected.filter((r) => pickedKeys.has(String(r.__pick)))).filter(isItemSrc)
    },
    [selectedItems, picked],
  )

  const { onDownloadExcel, isLoading: isDownloading } = useExcelDownload<Record<string, unknown>>({
    fileNamePrefix: '입고조회',
    config: { method: 'post' },
    errorToast: true,
  })

  /** 사유 모달 폼 값 검증 — 옛 모달은 [확인] 을 잠가 막았다. 생성본 모달에는 그 잠금이 없어 여기서 같은 선을 지킨다(실패 = throw, 모달 유지). */
  const readReason = (ctx: ActionCtx): string => {
    const reason = asText(ctx.form?.reason)
    const detail = asText(ctx.form?.detail).trim()
    if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH) {
      throw invalidError(MESSAGES.downloadValidation)
    }
    if (detail.length > REASON_DETAIL_MAX_LENGTH) {
      throw invalidError(MESSAGES.downloadTooLong)
    }
    return toDetailedReason({ reasonCode: reason, reasonDetail: detail })
  }

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(문서 51). ──
  /** [바코드 인쇄] 실행부 — 검증 실패·팝업 차단은 throw. 슬롯이 프로미스 체인 안에서 부른다(아래). */
  const printSelectedBarcodes = (ctx: ActionCtx): false => {
    const rows = itemTargets(ctx)
    const spec = asText(ctx.form?.spec) || DEFAULT_PRINT_SPEC
    if (!isImplementedSpec(spec)) {
      throw invalidError(MESSAGES.printSpecUnsupported)
    }
    if (printableRows(rows).length === 0) {
      throw invalidError(MESSAGES.printNoBarcodeRows)
    }
    // Number(…) || 기본값: NaN·0·빈 값 → 1매, Math.max(1, …): 음수 방어 — 둘 다 필요하다(Math.max(1, NaN) 은 NaN).
    const copies = Math.max(1, Number(ctx.form?.copies) || DEFAULT_PRINT_COPIES)
    // 팝업이 차단돼 인쇄 창을 못 열었으면 실패로 끝낸다(throw) — 모달이 열린 채 남아 팝업을 허용한 뒤 다시 누를 수 있다(안내는 인쇄 모듈이 띄웠다).
    if (!openBarcodePrintWindow(rows, spec, () => copies)) throw new Error(MESSAGES.printPopupBlocked)
    return false
  }

  const wiring: Wiring = {
    // 화면관리 그리드 설정(LOGISPRD-17250) — 표마다 다른 그리드(1.0 GR02_HEAD·GR02_ITEM 짝). 표 id 는 columnLayout 키와 같다
    permission: usePermissionSlot('gr09', GRID_CDS),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { inqHeadTable: coreTableLayoutSlot(GRID_CDS.inqHeadTable), inqItemTable: coreTableLayoutSlot(GRID_CDS.inqItemTable) },
    rows: {
      /**
       * 상단 — 전량({ rows } · total 없음). total 을 안 주면 이 뷰는 받은 행을 1만 건씩 자르고 목록 검색도 받은 행 전체에 건다.
       * 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 나머지 구간은 받는 대로 뒤에 붙고 '전체 N'·페이지 수가 늘어나는 게 진행 표시다.
       */
      ROWS: () => ({ rows: headRows, loading: isHeadFirstLoading || isHeadFetchingRest }),
      /** 하단 — 고른 입고 건의 품목 전건(서버 페이징 없음, 건마다 요청이라 loading 을 같이 준다). 뷰가 `__pick` 으로 상단 선택과 잇고 페이지를 나눈다. */
      ROWS2: () => ({ rows: details.rows, loading: details.loading }),
    },
    actions: {
      /** [조회] — 조회영역 값을 BE 조건으로. 검증에 걸리면 안내하고 false(뷰가 페이저·선택·검색칸을 되돌리지 않는다). */
      search: (filters) => {
        const values = toFormValues(filters)
        // 화주 고정 계정은 조회 경로에서도 고를 수 있는 화주로 강제한다 — 옵션 제한만으로는 '전체' 선택으로 화주명이 비어 유형만으로 나갈 수 있다(화주 잠금 4곳, 가이드 52 §5-A 5).
        if (!isFreeShipper && viewerShipperCode) values.shipperCode = lockedShipperCodes(asList(values.shipperCode))
        if (hasTooManyShipperNames(values)) {
          notice.invalid(MESSAGES.tooManyShipperNames)
          return false
        }
        // 화주 고정 계정은 고른 화주의 유형으로 조회한다 — 화주를 바꾼 직후 칸에 남은 이전 화주 유형을 싣지 않는다(LOGISPRD-15608).
        //  고를 화주가 하나뿐인 계정은 종전처럼 칸 값 그대로 보낸다.
        const lockedType = canSelectShipper(scope) && !isFreeShipper ? lockedServiceTypeOf(values.shipperCode) : undefined
        if (lockedType) values.serviceTypes = [lockedType]
        // 화주유형을 바꿔도 이미 고른 화주명은 칸에 남는다(뷰는 값을 옵션과 대조하지 않고, 옛 훅 useShipperNameFilterOptions 의 '못 고르는 화주명 지우기' 가 없다) —
        // 그 조합은 항상 0건이라 조회 전에 막는다(inventoryStatus 선례, 리뷰 2026-09-29). 유형을 실제로 고른 경우에만 대조한다 — 화주명 단독 조회는 정상 경로.
        // 화주 목록이 아직 없으면(로딩) 대조할 수 없어 통과시킨다 — 유형별 옵션이 비어 있으면 판정하지 않는다.
        const typesPicked = asList(values.serviceTypes)
        const namePicked = asList(values.shipperCode)[0]
        if (typesPicked.length > 0 && namePicked) {
          const allowed = buildShipperNameOptions(shipperOptionSources, { valueKey: 'shipperCode', serviceTypes: typesPicked }).map((o) => o.value)
          if (allowed.length > 0 && !allowed.includes(namePicked)) {
            notice.invalid(MESSAGES.shipperNameNotInType)
            return false
          }
        }
        const condition = toInboundsReq(values)
        if (!hasRequiredFilter(condition)) {
          notice.invalid(MESSAGES.requiredFilter)
          return false
        }
        if (exceedsOrdOptionNumberLimit(condition)) {
          notice.invalid(MESSAGES.ordOptionNumberLimitExceeded)
          return false
        }
        // 사용자 조회가 복원 조회를 대신한다 — 복원 effect 가 이 조건을 옛 복원 조건으로 덮지 않게 잠근다.
        isRestoreSearchedRef.current = true
        lastSearchValuesRef.current = values
        setPicked([])
        keywordRef.current = ''
        persistSearch(values, '')
        // 클릭 문맥이라 flushSync — 조건이 커밋되기 전에 다시 받으면 이전 조건으로 읽는다.
        flushSync(() => setSearchCondition(condition))
        // 같은 조건이어도 서버에 다시 묻는다(1.0) — 키가 같으면 캐시라 켜기만으로는 안 나간다. 조건이 바뀌어 새 키가 스스로 받는 중이면
        //  cancelRefetch: false 로 거기에 합류한다(취소하고 새로 띄우면 취소된 요청도 서버까지 가서 조회가 두 번 나간다).
        // 실패 알럿은 공용 훅이 전역으로 띄운다. 0건 안내는 [조회] 경로에서만(복원 진입에선 부르지 않은 알럿이 된다).
        return refetchHead({ cancelRefetch: false }).then((res) => {
          if (!res.isError && (res.data?.[0]?.totalCount ?? 0) === 0) notice.invalid(MESSAGES.searchNoRows)
        })
      },
      /**
       * [입고취소] 확인 — 전건을 끝까지 시도한다(중간 실패로 멈추지 않는다, R-08). 결과 요약은 토스트(반환 문자열), 실패 상세는 알럿.
       * 성공이 0건이든 전건이든 끝나면 재조회한다(선택 해제는 생성본이 한다).
       */
      inqCancelConfirm_ok: async (ctx) => {
        // 받는 중·일부 실패·조회 실패면 안내하고 확인창을 유지한다(throw — false 는 생성본이 성공으로 읽는다)
        if (!guardAction()) throw new Error(FETCH_ALL_MESSAGES.fetching)
        const rows = headTargets(ctx)
        if (rows.length === 0) {
          throw invalidError(MESSAGES.noHeadSelection)
        }
        // opens 가드(hdCancelButton)와 같은 검사 — 슬롯이 가드 없이 불려도 차수 없는 건이 cutSequence: null 로 나가지 않게(봇 리뷰 2026-09-29).
        const targets = rows.filter((r): r is GetInboundsItem & { cutSeq: number } => r.cutSeq != null)
        if (targets.length !== rows.length) {
          throw invalidError(MESSAGES.cancelMissingCutSeq)
        }
        setIsCancelling(true)
        let successCount = 0
        const failMessages: string[] = []
        try {
          // 한 건씩 순서대로(BE 단건 API) — 앞 건이 실패해도 다음 건을 계속 보낸다.
          await targets.reduce(async (prev, row) => {
            await prev
            const body: CancelInboundReq = {
              shipperName: row.shipperCode,
              cutDate: toCutDate(row.clsDate),
              cutSequence: row.cutSeq,
              orderOptionNo: row.ordOptionNumber,
            }
            try {
              await cancelInboundMutateAsync(body)
              successCount += 1
            } catch (error) {
              failMessages.push(`${row.ordOptionNumber}: ${extractErrorMessage(error, MESSAGES.cancelFailed)}`)
            }
          }, Promise.resolve())
        } finally {
          setIsCancelling(false)
        }
        // 체크 선택은 비우고(취소된 건이 [바코드 인쇄] 대상으로 남지 않게) 마스터 선택(picked)은 둔다 — 뷰의 확인창 뒷정리가 pick 을 풀고 onPick([]) 으로 알린다(pbo-design #643).
        //  대신 품목 쿼리를 무효화해 취소 반영값으로 다시 읽는다(리뷰 2026-09-29).
        setSelectedHeads([])
        setSelectedItems([])
        await queryClient.invalidateQueries({ queryKey: [...inboundInquiryQueryKeys.all, 'ITEM'] })
        // 같은 조건으로 다시 받는다 — 실패 알럿은 공용 훅이 띄운다.
        await refetchHead()
        const summary = MESSAGES.cancelSucceeded(successCount, failMessages.length)
        if (failMessages.length) {
          // 실패가 있으면 요약+상세를 알럿 하나로(줄바꿈 유지) 보이고 false — 성공 토스트는 막되 뷰의 뒷정리(선택 비움·확인창 닫기)는 그대로 돈다(리뷰 2026-09-29).
          notice.failure([summary, ...failMessages].join('\n'))
          return false
        }
        return summary
      },
      /** 상단 엑셀 — 조회 조건 기준(/excel, 서버가 page/size 를 무시). 성공 토스트는 없다(false = 성공인데 토스트 생략). */
      inqHeadDownloadModal_ok: async (ctx) => {
        const detailedReason = readReason(ctx)
        await onDownloadExcel(
          `${GET_INBOUNDS_EXCEL_URL}?${new URLSearchParams({ detailedReason }).toString()}`,
          (searchCondition ?? {}) as Record<string, unknown>,
        )
        return false as const
      },
      /** 하단 엑셀 — 고른 입고 건 하나의 품목 기준. BE 가 입고 건 하나만 받아 여러 건을 고른 채로는 열지 않는다(opens.itExcelButton). */
      inqItemDownloadModal_ok: async (ctx) => {
        const detailedReason = readReason(ctx)
        const params = detailParams.length === 1 ? detailParams[0] : null
        if (!params) {
          throw invalidError(MESSAGES.itemExcelSinglePick)
        }
        await onDownloadExcel(
          `${GET_INBOUND_DETAILS_EXCEL_URL}?${new URLSearchParams({ detailedReason }).toString()}`,
          params as unknown as Record<string, unknown>,
        )
        return false as const
      },
      /**
       * [바코드 인쇄] 확인 — 모달 폼(spec 규격 · copies 매수)으로 선택 품목의 라벨을 찍는다. **화면에 보이는 값 그대로** — 손대지 않은 칸은
       * 생성본 defaultValue(규격 A4(3X8) · 매수 1)를 쓰고, 매수는 행마다 같은 장수로 찍는다(표시값 = 실제 인쇄. 옛 모달의 "미입력이면 행별
       * 지시수량" 규칙은 기본 표시가 1 매인 데모와 어긋나 버렸다 — PD 확인 대상, 리뷰 2026-09-29). 지원하지 않는 규격·바코드 없음은 throw(모달 유지).
       */
      inqBarcodePrintModal_ok: (ctx) => Promise.resolve().then(() => printSelectedBarcodes(ctx)),
      // 프로미스 체인 안에서 실행한다 — 뷰는 `Promise.resolve(fn())` 로 감싸기만 해서 동기 throw 는 잡지 못한다(콘솔 uncaught, 리뷰 2026-09-29). 컨덕터 후속 전까지의 우회.
    },
    useBusy: () => isCancelling || isDownloading,
    /** 바코드 출력 모달 폼 — 손대지 않은 칸을 화면 기본값으로 채워 확인 슬롯이 보이는 값을 받게 한다(위 상수 주석). */
    form: { inqBarcodePrintModal: (state) => ({ spec: DEFAULT_PRINT_SPEC, copies: DEFAULT_PRINT_COPIES, ...state }) },
    /**
     * 상단 행 클릭(pick) = 마스터. 차수(cutSeq)가 없는 건은 품목을 조회할 수 없어 안내하고 뺀다(R-09).
     * 품목 행(`clsDate` 없음)이 오면 마스터 선택으로 취급하지 않는다 — 옛 생성본은 하단 표 체크에서도 onPick 을 불러 상단 선택이 지워졌다
     * (사전 리뷰 Warning 2026-09-28, codegen 은 pbo-design#633 으로 고침 · 방어는 남긴다).
     */
    onPick: (rows) => {
      // rows 가 비면 pick 해제([조회]·[초기화]·확인창 뒷정리) — 어느 표 행인지 검사 없이 통과한다. 행이 있으면 상단(HEAD) 행일 때만 받는다.
      if (rows.length && !rows.every((r) => isHeadSrc(r.__src))) return
      const heads = srcOf<GetInboundsItem>(rows)
      if (heads.some((h) => h.cutSeq == null)) notice.invalid(MESSAGES.itemMissingCutSeq)
      setPicked(heads.filter((h) => h.cutSeq != null))
      // 하단 체크 선택은 비우지 않는다 — itemTargets 가 지금 건(__pick)의 행만 고르므로 다른 건 품목은 빠지고, 돌아오면 다시 대상이 된다.
    },
    onSelect: (rows, scope) => {
      if (scope === HD_SCOPE) setSelectedHeads(srcOf<GetInboundsItem>(rows))
      else if (scope === IT_SCOPE) {
        // 뷰의 선택 키 집합을 그대로 거울처럼 따른다(통째 교체) — 배선이 건별 기록을 따로 보존하면 뷰와 갈라진다(리뷰 2026-09-29 두 건):
        //  · 뷰의 하단 표는 건이 바뀌어도 체크 키를 들고 있다(내부 state, 다시 알리지 않음) → 체크 없이 건만 오갔다 돌아오면 체크가 보이고,
        //    배선도 그 기록을 그대로 들고 있어(onPick 은 비우지 않는다) 인쇄 대상이 맞는다.
        //  · 다른 건에서 체크를 한 번 누르면 antd 가 화면에 없는 키를 버린다(preserveSelectedRowKeys 없음) → 뷰가 알린 값이 곧 새 진실이라 통째 교체.
        //  어느 건의 행인지(__pick)는 함께 들어 두어 itemTargets 가 지금 고른 건의 행만 쓴다. 건이 바뀔 때 하단 체크를 확정적으로 비우는 것은 뷰(컨덕터) 후속.
        setSelectedItems(rows.flatMap((r) => (isItemSrc(r.__src) ? [{ pick: String(r.__pick), src: r.__src }] : [])))
      }
      // 모르는 scope 면 아무것도 지우지 않는다 — 선택을 푼 신호는 rows 가 빈 배열이라 행만 보고는 어느 표인지 알 수 없다.
    },
    opens: {
      /** [입고취소] — 선택 없음·차수 없음·LES 원천(LES 에서 취소해야 하는 건)은 확인창을 열지 않는다(옛 handleCancelClick 게이트). */
      hdCancelButton: (ctx) => {
        if (!guardAction()) return false
        const rows = headTargets(ctx)
        if (rows.length === 0) {
          notice.invalid(MESSAGES.noHeadSelection)
          return false
        }
        if (rows.some((r) => r.cutSeq == null)) {
          notice.invalid(MESSAGES.cancelMissingCutSeq)
          return false
        }
        const blockedReason = lesGate.blockedReason(rows)
        if (blockedReason) {
          notice.invalid(blockedReason)
          return false
        }
        return true
      },
      /** 상단 엑셀 — 조회 결과가 없거나 BE 상한(30,000행)을 넘으면 열지 않는다. */
      hdExcelButton: () => {
        // 받는 중·일부 실패면 건수가 서버와 달라 0건·상한 판정이 틀린다(안내는 guardAction 이 띄운다).
        if (!guardAction()) return false
        if (headTotal === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        if (headTotal > EXCEL_ROW_LIMIT) {
          notice.invalid(MESSAGES.excelRowLimitExceeded)
          return false
        }
        return true
      },
      itPrintButton: (ctx) => {
        if (itemTargets(ctx).length === 0) {
          notice.invalid(MESSAGES.noItemSelection)
          return false
        }
        return true
      },
      /** 하단 엑셀 — 품목이 없거나 상단에서 여러 건을 고른 상태면 열지 않는다(BE 는 입고 건 하나만 받는다). */
      itExcelButton: () => {
        if (details.rows.length === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        if (detailParams.length !== 1) {
          notice.invalid(MESSAGES.itemExcelSinglePick)
          return false
        }
        return true
      },
    },
    options: {
      INQSHIPPER_TYPE_OPTIONS: (_demo, filters) =>
        isFreeShipper ? serviceTypeOptions : lockedServiceTypeOptions.get(scopedShipperCode(scope, asList(filters?.[FK.names])[0])) ?? EMPTY_OPTIONS,
      // 화주명은 선택한 화주유형으로 좁히고 값은 화주**코드**다(BE `shipperCode` 는 UI_GRREPORT_DETAIL.SPR_NM 완전일치이고 SPR_NM 은 화주코드). 라벨은 화주명에 화주코드를 붙인다.
      //  화주 고정 계정은 유형으로 좁히지 않는다 — 유형 칸이 고른 화주를 따르므로 좁히면 다른 화주(MUSINSA)를 고를 수 없다(LOGISPRD-15608).
      INQSHIPPER_NAME_OPTIONS: (_demo, filters) =>
        buildShipperNameOptions(shipperOptionSources, { valueKey: 'shipperCode', serviceTypes: isFreeShipper ? asList(filters?.[FK.types]) : [] }),
      // 입고유형은 moms-be `OrderType.displayName` 실측(constants.ts) — 데모 목록의 폐기·테스트2·증정·재고이동·반품은 2.0 이 막거나 항상 0건인 값이다.
      INQORDTYPE_OPTIONS: () => ORDER_TYPE_OPTS,
      INQWHNM_OPTIONS: () => warehouseNameOptions,
      SPECSELECT_OPTIONS: () => PRINT_SPEC_OPTS,
      REASONRADIO_OPTIONS: () => reasonOptions,
      REASONRADIO_OPTIONS2: () => reasonOptions,
    },
    filterDefaults: { inqFilterCard: (demo) => ({ ...demo, ...defaultFilterValues(isFreeShipper, defaultShipperCode) }) },
    filterInitial: { inqFilterCard: (defaults) => ({ ...defaults, ...toFilterValues(restored?.values) }) },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·fallback·search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15608)
     */
    filterDisabled: isFreeShipper ? undefined : { service_type: true, shipper_name: !canSelectShipper(scope) },
    /** 화주 고정 계정의 화주유형은 화주 목록 응답이 온 뒤에야 정해진다 — 비어 있을 때만 고른 화주의 유형으로 채운다(값이 있는 키는 덮지 않는다). */
    filterFallback: {
      inqFilterCard: (current) => {
        const serviceType = isFreeShipper ? undefined : lockedServiceTypeOf(current[FK.names])
        return serviceType && asList(current[FK.types]).length === 0 ? { [FK.types]: [serviceType] } : undefined
      },
    },
    // 하단(품목) 페이저는 뷰 기본값(전량, MOMS 공통 규칙)에 둔다 — 50건으로 자르면 다른 입고 건을 골라도 페이지가 남아 품목이 있는데 빈 표가 떴다(리뷰 2026-09-29).
    stateInitial: { listq: { 'hd-table.q': restored?.keyword ?? '' } },
    /**
     * 화면 상태 알림 — 목록 검색어 확정만 세션·URL 에 저장한다. 페이지 이동은 받은 행 안에서라 할 일이 없다
     * (마스터 선택은 뷰의 상단 페이저가 pick 을 풀고 onPick([]) 으로 알린다, pbo-design #643). 마운트·조회 뒤에도 불리므로 직전 값과 같으면 넘긴다.
     */
    onState: (s) => {
      const keyword = s.listq['hd-table.q']
      if (keyword === keywordRef.current) return
      keywordRef.current = keyword
      persistSearch(lastSearchValuesRef.current, keyword)
    },
    /**
     * [초기화] — 저장된 조회 조건(세션·URL)과 조회 결과를 함께 비운다. 다음 진입이 기본값으로 열린다.
     * 서버 페이징을 쓰는 다른 핸드오프 화면(GR05 입고조회(전체)·EGR 일반입고(ERP)·기타출고·합배송출고)과 같은 규칙(PD 확인 2026-09-29).
     * 뷰는 같은 클릭에서 목록 검색어를 '' 로 되돌리고 다음 렌더에 onState 로 알린다 — ref 를 먼저 그 값에 맞춰 두어
     * onState 가 이를 검색어 변경으로 읽고 방금 지운 조건을 다시 저장하지 않게 한다([조회] search 와 같은 순서, 리뷰 2026-09-29).
     * 조건을 null 로 두면 조회가 꺼진다(빈 키 = 빈 표).
     */
    onReset: () => {
      keywordRef.current = ''
      lastSearchValuesRef.current = toFormValues(defaultFilterValues(isFreeShipper, defaultShipperCode))
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      isRestoreSearchedRef.current = true
      setSearchCondition(null)
      setPicked([])
      setSelectedHeads([])
      setSelectedItems([])
    },
  }

  // 타이틀 행·조회영역·표·모달은 뷰가 그린다 — 여기서 또 그리면 제목이 두 번 선다.
  return <InboundInquiryPageView wiring={wiring} />
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 화면을 연다 — 화주 범위·URL 복원값은 마운트 첫 렌더에 한 번 만들어지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15608).
 */
function InboundInquiryEntry() {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <InboundInquiry coMingle={coMingle} />
}

export default InboundInquiryEntry
